import { createClient } from '@supabase/supabase-js';
import type { Session } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL ?? '',
  import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''
);

interface AuthState {
  user: any | null;
  organization: any | null;
  setUser: (user: any | null) => void;
  setOrganization: (organization: any | null) => void;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      organization: null,
      setUser: (user) => set({ user }),
      setOrganization: (organization) => set({ organization }),
    }),
    {
      name: 'auth-storage',
    }
  )
);

const SupabaseContext = createContext<typeof supabase | null>(null);

export function SupabaseProvider({ children }: { children: React.ReactNode }) {
  const { setUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const refreshTimeout = React.useRef<NodeJS.Timeout>();
  const loadOrganizationUser = React.useCallback(
    async (userId: string) => {
      const trySelect = (select: string) =>
        supabase
          .from('organization_users')
          .select(select)
          .eq('user_id', userId)
          .single();

      let result = await trySelect('role, is_super_user, full_name');

      if (result.error) {
        const message = result.error.message ?? '';

        if (message.includes('full_name')) {
          result = await trySelect('role, is_super_user');
        }
      }

      return result;
    },
    []
  );

  useEffect(() => {
    const initializeAuth = async () => {
      // Check active sessions and sets the user
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        setError(error.message);
      } else {
        setSession(session ?? null);
        if (session?.user) {
          // Set up session refresh
          const expiresAt = session.expires_at;
          if (expiresAt) {
            const timeUntilExpiry = (expiresAt - Date.now() / 1000) * 1000;
            const refreshTime = Math.max(timeUntilExpiry - 60000, 0); // Refresh 1 minute before expiry
            
            refreshTimeout.current = setTimeout(async () => {
              const { error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError) {
                console.error('Error refreshing session:', refreshError);
                navigate('/login');
              }
            }, refreshTime);
          }

          // Get organization membership and roles
          const { data: orgUser, error: orgError } = await loadOrganizationUser(session.user.id);
          const metadataName = session.user.user_metadata?.full_name ?? session.user.user_metadata?.name ?? null;
          const organizationName = orgUser?.full_name ?? orgUser?.name ?? null;

          if (!orgError && orgUser) {
            setUser({
              ...session.user,
              org_role: orgUser.role,
              is_super_user: orgUser.is_super_user,
              full_name: organizationName ?? metadataName ?? null,
              name: orgUser.name ?? null,
            });
          } else {
            setUser({
              ...session.user,
              full_name: metadataName ?? null,
            });
          }
          
          // Handle redirection for invited users
          if (session.user.user_metadata?.redirectTo === 'setup-password' && !isRedirecting) {
            setIsRedirecting(true);
            navigate('/setup-password');
          }
        } else {
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
    
    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };

    // Listen for changes on auth state
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setSession(session);
        // Set up session refresh for new session
        const expiresAt = session.expires_at;
        if (expiresAt) {
          if (refreshTimeout.current) {
            clearTimeout(refreshTimeout.current);
          }

          const timeUntilExpiry = (expiresAt - Date.now() / 1000) * 1000;
          const refreshTime = Math.max(timeUntilExpiry - 60000, 0);

          refreshTimeout.current = setTimeout(async () => {
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('Error refreshing session:', refreshError);
              navigate('/login');
            }
          }, refreshTime);
        }

        // Get organization membership and roles
        const { data: orgUser, error: orgError } = await loadOrganizationUser(session.user.id);
        const metadataName = session.user.user_metadata?.full_name ?? session.user.user_metadata?.name ?? null;
        const organizationName = orgUser?.full_name ?? orgUser?.name ?? null;

        if (!orgError && orgUser) {
          setUser({
            ...session.user,
            org_role: orgUser.role,
            is_super_user: orgUser.is_super_user,
            full_name: organizationName ?? metadataName ?? null,
            name: orgUser.name ?? null,
          });
        } else {
          setUser({
            ...session.user,
            full_name: metadataName ?? null,
          });
        }

        // Handle redirection for invited users
        if (session.user.user_metadata?.redirectTo === 'setup-password' && !isRedirecting) {
          setIsRedirecting(true);
          navigate('/setup-password');
        }
      } else {
        setUser(null);
        setSession(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, setUser, isRedirecting]);

  // Add effect to handle logout redirects
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Redirect to login page when user signs out
        navigate('/login', { replace: true });
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 dark:text-red-400 text-center max-w-md mx-auto px-4">
          <p className="text-lg font-semibold">Please connect to Supabase</p>
          <p className="mt-2 text-sm">Click the "Connect to Supabase" button in the top right corner to set up your project.</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    let canceled = false;

    const syncSessionWithServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (canceled) return;

        registration.active?.postMessage({
          type: 'SUPABASE_SESSION',
          payload: session
            ? {
                accessToken: session.access_token,
                refreshToken: session.refresh_token ?? null,
                userId: session.user.id,
              }
            : null,
        });
      } catch (syncError) {
        console.warn('Failed to sync auth session with service worker', syncError);
      }
    };

    syncSessionWithServiceWorker();

    return () => {
      canceled = true;
    };
  }, [session]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <SupabaseContext.Provider value={supabase}>
      {children}
    </SupabaseContext.Provider>
  );
}

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

export const useAuth = () => {
  const { user, organization, setUser, setOrganization } = useAuthStore();
  return { user, organization, setUser, setOrganization };
};
